'use client';

import { useEffect, useRef } from 'react';

interface CodeBlockWrapperProps {
  children: React.ReactNode;
}

// CSS styles as a string to inject
const codeBlockStyles = `
  /* Code Block Container */
  .code-block-container {
    border-radius: 0.3em;
    overflow: hidden;
    margin: 0.75rem 0 0.5rem 0;
  }
  
  /* Code Block Header - slightly lighter than code area */
  .code-block-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1em;
    background: rgba(64, 73, 88, 1);
    border-bottom: 1px solid rgba(171, 178, 191, 0.15);
  }
  
  /* TEXT Label */
  .code-block-label {
    color: rgb(171, 178, 191);
    font-family: "Fira Code", "Fira Mono", Menlo, Consolas, "DejaVu Sans Mono", monospace;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
  }
  
  /* Copy Button */
  .code-block-copy-btn {
    background-color: rgba(171, 178, 191, 0.1);
    color: rgb(171, 178, 191);
    border: 1px solid rgba(171, 178, 191, 0.2);
    padding: 0.25rem 0.6rem;
    border-radius: 4px;
    font-family: "Fira Code", "Fira Mono", Menlo, Consolas, "DejaVu Sans Mono", monospace;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .code-block-copy-btn:hover {
    background-color: rgba(171, 178, 191, 0.2);
    color: #ffffff;
  }
  
  /* Override prose pre styles - match reference exactly */
  .code-block-container .prose pre,
  .code-block-container pre {
    background: rgb(40, 44, 52) !important;
    color: rgb(171, 178, 191) !important;
    font-family: "Fira Code", "Fira Mono", Menlo, Consolas, "DejaVu Sans Mono", monospace !important;
    direction: ltr;
    text-align: left;
    white-space: pre;
    word-spacing: normal;
    word-break: normal;
    line-height: 1.3 !important;
    tab-size: 2;
    hyphens: none;
    padding: 1.5em 1em !important;
    margin: 0 !important;
    overflow: auto;
    border-radius: 0 !important;
    font-size: 14px;
    text-shadow: rgba(0, 0, 0, 0.3) 0px 1px;
    border: none !important;
  }
  
  /* Code content style - match reference exactly */
  .code-block-container pre code {
    white-space: pre;
    background: rgb(40, 44, 52) !important;
    color: rgb(171, 178, 191);
    font-family: "Fira Code", "Fira Mono", Menlo, Consolas, "DejaVu Sans Mono", monospace !important;
    direction: ltr;
    text-align: left;
    word-spacing: normal;
    word-break: normal;
    line-height: 1.3 !important;
    tab-size: 2;
    hyphens: none;
    text-shadow: rgba(0, 0, 0, 0.3) 0px 1px;
    font-size: 14px;
    padding: 0 !important;
    display: block;
  }
  
  /* Mobile-specific code block styles - MUST BE LAST for proper override */
  @media (max-width: 768px) {
    .code-block-container .prose pre,
    .code-block-container pre {
      white-space: pre !important;
      overflow-x: auto !important;
      font-size: 11px !important;
      padding: 1.25em 0.75em !important;
    }
    
    .code-block-container pre code {
      white-space: pre !important;
      font-size: 11px !important;
      display: inline-block !important;
      padding-right: 2em !important;
      padding-left: 0.5em !important;
    }
    
    .code-block-header {
      padding: 0.4rem 0.75em !important;
    }
    
    .code-block-label {
      font-size: 11px !important;
    }
    
    .code-block-copy-btn {
      font-size: 10px !important;
      padding: 0.2rem 0.5rem !important;
    }
  }

  /* Preserve syntax highlighting colors */
  .code-block-container pre code .syn-keyword {
    color: #02a3df;
  }
  .code-block-container pre code .syn-string {
    color: #906bc6;
  }
  .code-block-container pre code .syn-comment {
    color: #906bc6;
    font-style: italic;
  }
  .code-block-container pre code .syn-number {
    color: #5eacae;
  }
  .code-block-container pre code .syn-command {
    color: #e5a248;
  }
  .code-block-container pre code .syn-extension {
    color: #7ec8a4;
  }
  .code-block-container pre code .syn-operator {
    color: #8be9fd;
  }
  .code-block-container pre code .syn-bracket {
    color: #bd93f9;
  }
`;

export default function CodeBlockWrapper({ children }: CodeBlockWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stylesInjectedRef = useRef(false);

  useEffect(() => {
    // Inject styles only once
    if (!stylesInjectedRef.current) {
      const styleElement = document.createElement('style');
      styleElement.setAttribute('data-code-block-styles', 'true');
      styleElement.textContent = codeBlockStyles;
      document.head.appendChild(styleElement);
      stylesInjectedRef.current = true;
    }

    if (!containerRef.current) return;

    // Find all pre elements and wrap them with the custom header
    const preElements = containerRef.current.querySelectorAll('pre');

    preElements.forEach((pre, index) => {
      // Skip if already wrapped
      if (pre.parentElement?.classList.contains('code-block-container')) return;

      // Create wrapper container
      const wrapper = document.createElement('div');
      wrapper.className = 'code-block-container';

      // Create header
      const header = document.createElement('div');
      header.className = 'code-block-header';

      // Create label
      const label = document.createElement('span');
      label.className = 'code-block-label';
      label.textContent = 'TEXT';

      // Create copy button
      const copyBtn = document.createElement('button');
      copyBtn.className = 'code-block-copy-btn';
      copyBtn.textContent = 'Copy';
      copyBtn.setAttribute('data-index', index.toString());

      copyBtn.addEventListener('click', async () => {
        const code = pre.querySelector('code');
        const text = code?.textContent || pre.textContent || '';

        try {
          await navigator.clipboard.writeText(text);
          copyBtn.textContent = 'Copied!';
          setTimeout(() => {
            copyBtn.textContent = 'Copy';
          }, 2000);
        } catch (err) {
          console.error('Failed to copy:', err);
        }
      });

      header.appendChild(label);
      header.appendChild(copyBtn);

      // Wrap the pre element
      pre.parentNode?.insertBefore(wrapper, pre);
      wrapper.appendChild(header);
      wrapper.appendChild(pre);
    });

    // Cleanup function
    return () => {
      // Don't remove styles on cleanup as they may be needed by other instances
    };
  }, [children]);

  return <div ref={containerRef}>{children}</div>;
}
